import { OutOfDateGuard, refusedOrVersion, withBasedOn } from 'api/keptVersion';
import { leaveSignedOut } from 'utils/signedOut';
import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import type { AccountSettingsForm } from 'views/settings/account/AccountSettings';
import {
  AccountSettingsFormData,
  buildAccountSettingsData,
} from 'views/settings/account/components/settingsPayload';

/**
 * The name card of the account screen stores itself (auto-save).
 *
 * One of the four screens without a publish moment (D34): what is stored
 * here is in use at once (the name and signature under mails to candidates).
 * So while a required field is empty the card is not stored at all, and the
 * corner says why (D35). The password card next to it keeps its button: a
 * password changes when "Change password" is pressed, never by itself
 * (scene 16).
 */

/** What is missing, in the words shown after "Not saved: ". */
export const missingForNameCard = (work: AccountSettingsFormData): string[] => [
  ...(String(work.firstname ?? '').trim() === ''
    ? ['first name is required']
    : []),
  ...(String(work.lastname ?? '').trim() === ''
    ? ['last name is required']
    : []),
];

export const ACCOUNT_PATH = '/v4/users/settings';

/** The card as the server takes it: the same body the Save button sent. */
export const nameCardBody = (
  fields: AccountSettingsForm[],
  work: AccountSettingsFormData
) => buildAccountSettingsData(fields, work);

/** Stores the name card, quietly. @throws when the server did not confirm */
export const saveNameCard = async (
  fields: AccountSettingsForm[],
  work: AccountSettingsFormData,
  signal?: AbortSignal,
  guard?: OutOfDateGuard
): Promise<void> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';
  const response = await fetch(`${base}${ACCOUNT_PATH}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
      'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
    },
    body: JSON.stringify(
      withBasedOn(
        { ...nameCardBody(fields, work) } as Record<string, unknown>,
        guard?.basedOn
      )
    ),
    signal,
  });
  if (response.status === 401) {
    if (window.location.pathname !== '/') {
      leaveSignedOut();
    }
    throw new Error('The session has ended.');
  }
  // D10: out of date is not a failure; the person chooses.
  const version = await refusedOrVersion(response);
  if (!response.ok) {
    throw new Error(`The name card was not stored (${response.status}).`);
  }
  const answer = (await response.json().catch(() => ({}))) as {
    success?: boolean;
  };
  if (answer.success === false) {
    throw new Error('The name card was not stored.');
  }
  guard?.stored(version);
};
